import type { ListBuilderConfiguration } from '../types';

/**
 * Item List members configuration (PD-15839) — the table on
 * `/items/lists/:id`. Rows come from GET /item-lists/:id/items and are flat
 * item records keyed by `_id` (the same shape the Items grid gets), so the
 * name links straight to the item detail page.
 */
export const ITEM_LIST_ITEMS_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-item-list-items',
			cacheTTL: 600000,
			name: 'Items in List',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'image',
					width: 60,
					fields: [
						{
							id: 'item_image',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'primaryImageUrl', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								imageOptions: {
									size: 'small',
									enableViewer: true,
								},
							},
							displayStyle: 'image',
						},
					],
					headers: [{ id: 'image_header', text: '' }],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
				},
				{
					id: 'item-name',
					fields: [
						{
							id: 'description-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'item',
								idField: '_id',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'description', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'manufacturer-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'manufacturer', objectTable: 'item' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Item' }],
					minWidth: 220,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'top',
				},
				{
					id: 'item-number',
					fields: [
						{
							id: 'item-number-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'externalId', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'Item #' }],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', width: 140 },
					verticalAlignment: 'top',
				},
				{
					id: 'ndc',
					fields: [
						{
							id: 'ndc-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'ndc', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h3', text: 'NDC' }],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', width: 140 },
					verticalAlignment: 'top',
				},
				{
					id: 'category',
					fields: [
						{
							id: 'category-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'category', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h4', text: 'Category' }],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', width: 160 },
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'remove-action',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'remove-item-action',
									label: 'Remove',
									iconName: 'trash',
									callbackName: 'removeItem',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'h5', text: '' }],
					sortable: false,
					alignment: 'right',
					widthConfig: { mode: 'fixed', width: 80 },
					verticalAlignment: 'top',
				},
			],
			description: 'Items belonging to a specific item list',
			defaultSortId: 'name-asc',
			requiredFields: [
				'item._id',
				'item.description',
				'item.manufacturer',
				'item.externalId',
				'item.ndc',
				'item.category',
				'item.primaryImageUrl',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'externalId', fieldLabel: 'Item #' },
				{ fieldName: 'ndc', fieldLabel: 'NDC' },
				{ fieldName: 'category', fieldLabel: 'Category' },
				{ fieldName: 'primaryImageUrl', fieldLabel: 'Image' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name-asc',
			name: 'Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'description', objectTable: 'item' },
			],
			description: 'Sort items by name',
		},
	],
};
